import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useURLSearchParams } from "../../hooks";
import { Input, StatefulForm, useStatefulForm } from "../../component-library";
import { useUserQuery } from "../../queries";

interface ICreateUser {
    setQueryParamsLoaded: Dispatch<SetStateAction<boolean>>;
}

const schema = z.object({
    emailAddress: z.string().email("Invalid email address"),
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    tenantId: z.number().min(1, "Tenant is required"),
    roleId: z.number().min(1, "Role is required"),
});

type NewUser = z.infer<typeof schema>;

export function CreateUser({ setQueryParamsLoaded }: Readonly<ICreateUser>) {
    const navigate = useNavigate();
    const queryParams = useURLSearchParams();
    const {
        createMutation: { mutateAsync: createUser },
    } = useUserQuery();
    const [showCreateUserErrorMessage, setShowCreateUserErrorMessage] = useState<boolean>(false);
    const [showCreateUserSuccessMessage, setShowCreateUserSuccessMessage] = useState<boolean>(false);
    const defaultTenantId = 1;
    const defaultUserRoleId = 4;

    const defaultValues = useMemo<NewUser>(
        () => ({
            emailAddress: "",
            firstName: "",
            lastName: "",
            tenantId: defaultTenantId,
            roleId: defaultUserRoleId,
        }),
        []
    );

    const onSubmit = async (values: NewUser) => {
        setShowCreateUserErrorMessage(false);
        setShowCreateUserSuccessMessage(false);
        try {
            const response = await createUser(values);
            if (response) {
                setShowCreateUserSuccessMessage(true);
                let redirectPath = `/?emailAddress=${response.user.emailAddress}&tab=login`;
                const authCodeRedirect = queryParams.get("authCodeRedirect");
                if (authCodeRedirect) {
                    redirectPath += `&authCodeRedirect=${encodeURIComponent(authCodeRedirect)}`;
                }
                navigate(redirectPath);
                setQueryParamsLoaded(false);
            } else {
                setShowCreateUserErrorMessage(true);
            }
        } catch {
            setShowCreateUserErrorMessage(true);
        }
    };

    const { methods, isSubmitting } = useStatefulForm({ defaultValues, schema });

    return (
        <StatefulForm onSubmit={(values) => onSubmit(values as NewUser)} methods={methods}>
            <Input accessor="emailAddress" label="Email" type="email" />
            <Input accessor="firstName" label="First Name" />
            <Input accessor="lastName" label="Last Name" />
            <div className="text-end mb-2">
                <Button className="btn-primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Register"}
                </Button>
            </div>
            <Alert variant={"danger"} hidden={!showCreateUserErrorMessage}>
                Unable to create user.
            </Alert>
            <Alert variant="success" hidden={!showCreateUserSuccessMessage}>
                You have successfully registered! Please check your email for your login information.
            </Alert>
        </StatefulForm>
    );
}
